import { alpha, Theme } from '@mui/material/styles';

export const getCalendarStyles = (theme: Theme, isMobile: boolean) => ({
  height: isMobile ? 'calc(100vh - 250px)' : 'calc(100vh - 380px)',
  minHeight: isMobile ? 600 : 700,
  bgcolor: 'background.paper',
  p: isMobile ? 1 : 2,
  borderRadius: 3,
  boxShadow: '0 1px 3px rgba(0,0,0,0.08), 0 8px 24px rgba(0,0,0,0.04)',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'hidden',

  // Hide default toolbar (we use our custom one)
  '& .rbc-toolbar': { display: 'none' },

  // Global font
  '& .rbc-calendar': { fontFamily: theme.typography.fontFamily },

  // ─ Week / Day Header ─
  '& .rbc-header': {
    bgcolor: 'rgb(245, 245, 245)',
    p: 0,
    overflow: 'visible',
    minHeight: 64,
  },

  // ─ All-day row ─
  // Override colours must match the values used in dayPropGetter exactly
  // so the All-Day row never shows a different shade from the time-slot columns.
  '& .rbc-allday-cell': {
    maxHeight: '40px',
    borderTop: `2px solid rgb(0, 0, 0, 0.2)`,
    '& .rbc-past-day': {
      backgroundColor: '#F5F5F5 !important',
    },
    '& .rbc-non-working-day': {
      backgroundColor: 'rgba(255, 0, 0, 0.06) !important',
    }
  },

  // ─ Time Gutter Labels ─
  '& .rbc-time-gutter .rbc-label': {
    fontSize: '0.7rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    textAlign: 'center',
    padding: '0 10px',
    whiteSpace: 'nowrap',
  },
  '& .rbc-time-gutter, & .rbc-time-gutter .rbc-time-slot, & .rbc-time-gutter .rbc-timeslot-group': {
    backgroundColor: '#FFFFFF !important',
  },

  // ─ Today highlight ─
  // slotPropGetter sets explicit #FFFFFF on bookable slots; this overrides it
  // for slots inside today's column so the blue tint stays visible.
  '& .rbc-today': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
  },
  '& .rbc-today .rbc-time-slot:not(.rbc-past-slot):not(.rbc-non-working-slot)': {
    backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
  },
  // Month view specific today date circle
  '& .rbc-month-view .rbc-today .rbc-date-cell': {
    fontWeight: 'bold',
    '& .rbc-button-link': {
      color: 'white',
      bgcolor: theme.palette.primary.main,
      width: '28px',
      height: '28px',
      borderRadius: '50%',
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      margin: '4px',
      fontSize: '0.8rem',
      boxShadow: `0 2px 8px ${alpha(theme.palette.primary.main, 0.4)}`,
    }
  },

  // ─ Time slots ─
  '& .rbc-timeslot-group': {
    minHeight: '79px',
    borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
  },
  '& .rbc-time-slot': {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderBottom: 'none',
  },
  '& .rbc-day-slot .rbc-time-slot': {
    borderBottom: 'none',
    borderTop: '1px solid transparent'
  },
  '& .rbc-timeslot-group .rbc-time-slot:last-child': {
    borderBottom: `1px solid ${alpha(theme.palette.common.black, 0.2)}`,
  },

  // ─ Events ─
  '& .rbc-event': {
    border: 'none',
    borderRadius: '4px',
    boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
    transition: 'all 0.1s ease',
    padding: '1px 4px', // Tightened padding
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
    '&:focus': { outline: 'none' },
  },
  '& .rbc-event-label': { display: 'none' },
  '& .rbc-event-content': { fontSize: '0.8rem' },

  // ─ Time view container ─
  '& .rbc-time-view': {
    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    borderRadius: '12px',
    overflow: 'hidden',
    bgcolor: 'background.paper',
  },

  // ─ Day column borders ─
  '& .rbc-day-bg + .rbc-day-bg': {
    // borderLeft: `1px solid ${alpha(theme.palette.divider, 0.15)}`,
  },

  // ─ Month view ─
  '& .rbc-month-view': {
    border: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
    borderRadius: '12px',
    overflow: 'hidden',
    // Disable horizontal resizing in Month view
    '& .rbc-addons-dnd-resize-month-event-anchor': {
      display: 'none !important',
    },
  },
  '& .rbc-month-row': {
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.3)}`,
  },
  '& .rbc-month-header': {
    '& .rbc-header': {
      padding: '10px 0',
      fontWeight: 700,
      fontSize: '0.75rem',
      textTransform: 'uppercase',
      letterSpacing: '0.05em',
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
      textAlign: 'center',
    },
  },
  '& .rbc-date-cell': {
    textAlign: 'center',
    padding: '6px',
    '& button, & a': {
      fontWeight: 700,
      fontSize: '0.85rem',
      color: 'inherit',
      cursor: 'pointer',
    },
  },
  '& .rbc-off-range-bg': {
    bgcolor: alpha(theme.palette.action.disabledBackground, 0.3),
  },
  '& .rbc-off-range a, & .rbc-off-range button': {
    color: theme.palette.text.disabled,
  },

  // ─ Agenda view ─
  // Wrapper handles scrolling so thead can be sticky inside
  '& .rbc-agenda-view': {
    borderRadius: '16px',
    border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    bgcolor: 'background.paper',
    boxShadow: `0 20px 80px -16px ${alpha(theme.palette.common.black, 0.14)}`,
    maxHeight: isMobile ? 'calc(100vh - 320px)' : 'calc(100vh - 400px)',
    minHeight: isMobile ? 300 : 450,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    position: 'relative',
  },
  // Inner wrapper for scroll to allow sticky thead
  '& .rbc-agenda-view > div': {
    overflow: 'auto',
    flex: 1,
  },
  '& .rbc-agenda-view table': {
    width: '100%',
    borderCollapse: 'collapse',
    tableLayout: 'fixed',
  },
  '& .rbc-agenda-view table thead': {
    position: 'sticky',
    top: 0,
    zIndex: 10,
  },
  '& .rbc-agenda-view table thead th': {
    padding: isMobile ? '8px 12px !important' : '10px 22px !important', // Standardized to 20px
    fontWeight: 700,
    fontSize: isMobile ? '0.75rem' : '1rem',
    textTransform: 'uppercase',
    letterSpacing: isMobile ? '0.1em' : '0.25em',
    color: '#fff',
    bgcolor: theme.palette.primary.main,
    borderBottom: 'none',
    textAlign: 'left', // Aligned to left
    verticalAlign: 'middle !important',
    whiteSpace: 'nowrap',
    '&:last-of-type': {
      textAlign: 'left',
    },
    '&:not(:last-of-type)': {
      borderRight: `1px solid ${alpha(theme.palette.common.white, 0.15)}`,
    }
  },
  '& .rbc-agenda-view table tbody tr': {
    '&:hover': {
      bgcolor: alpha(theme.palette.primary.main, 0.02),
    },
  },
  '& .rbc-agenda-view table tbody td': {
    padding: theme.spacing(1.5, 2),
    [theme.breakpoints.up('sm')]: {
      padding: '24px 32px',
    },
    verticalAlign: 'middle !important',
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
  },
  '& .rbc-agenda-date-cell': {
    fontWeight: 700,
    fontSize: isMobile ? '0.85rem' : '1.1rem',
    color: theme.palette.text.primary,
    whiteSpace: 'nowrap',
    width: isMobile ? '100px' : '200px',
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
    textAlign: 'left',
    padding: isMobile ? '12px 12px !important' : '16px 15px !important',
  },
  '& .rbc-agenda-time-cell': {
    fontSize: isMobile ? '0.8rem' : '1rem',
    fontWeight: 600,
    color: theme.palette.text.secondary,
    whiteSpace: 'nowrap',
    width: isMobile ? '120px' : '240px',
    borderRight: `1px solid ${alpha(theme.palette.divider, 0.03)}`,
    textAlign: 'left',
    padding: isMobile ? '12px 12px !important' : '16px 20px !important',
  },
  '& .rbc-agenda-event-cell': {
    padding: '0 !important', // Clear parent padding to let event fill naturally
    cursor: 'pointer',
    textAlign: 'left',
    '& .rbc-event': {
      backgroundColor: 'transparent !important',
      color: `${theme.palette.text.primary} !important`,
      padding: isMobile ? '12px 12px !important' : '16px 20px !important', // Match header 20px
      marginLeft: '0 !important',
      cursor: 'pointer',
      '&:hover': {
        bgcolor: alpha(theme.palette.primary.main, 0.08),
        transform: 'translateX(10px)',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      },
      '& .MuiTypography-root': {
        color: 'inherit !important',
        fontSize: isMobile ? '0.8rem !important' : undefined,
        cursor: 'pointer',
      },
      '& .MuiSvgIcon-root': {
        color: 'inherit !important',
        opacity: 0.5,
        fontSize: isMobile ? '0.9rem !important' : undefined,
      }
    }
  },
  '& .rbc-agenda-event-cell .rbc-event:hover .MuiTypography-root:first-of-type': {
    textDecoration: 'underline',
    color: `${theme.palette.primary.main} !important`,
  },
  '& .rbc-agenda-empty': {
    textAlign: 'center',
    padding: isMobile ? '80px 24px' : '140px 48px',
    color: theme.palette.text.secondary,
    fontSize: isMobile ? '0.9rem' : '1.1rem',
    fontStyle: 'italic',
    letterSpacing: '0.02em',
  },

  // ─ Current time indicator ─
  '& .rbc-current-time-indicator': {
    bgcolor: theme.palette.error.main,
    height: '2px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: -4,
      top: -3,
      width: 8,
      height: 8,
      borderRadius: '50%',
      bgcolor: theme.palette.error.main,
    }
  },
});
